import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { toast } from "sonner";

const WARN_COLOR = {
  warn: "var(--mq-red)",
  info: "var(--mq-yellow)",
};

export default function CapitalAllocator() {
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [slider, setSlider] = useState(0);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/capital-allocator");
      setData(data);
      // initialise the slider to the AI default the first time
      setSlider((prev) => {
        const def = data?.recommended?.order_size_usdt_default ?? 0;
        return prev > 0 ? prev : def;
      });
    } catch (err) {
      console.warn("capital-allocator failed:", err?.message || err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 30_000);
    return () => clearInterval(id);
  }, [load]);

  const apply = async () => {
    if (!data || !slider || slider < (data.recommended?.order_size_usdt_min || 1)) return;
    setApplying(true);
    try {
      await api.put("/config", { overrides: { order_size_base_usdt: Number(slider) } });
      toast.success(t("capitalApplied"));
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || String(err));
    } finally {
      setApplying(false);
    }
  };

  if (!data) {
    return (
      <div className="mq-panel p-4" data-testid="capital-allocator-panel">
        <div className="mq-heading text-sm tracking-widest uppercase mb-2">
          {t("capitalTitle")}
        </div>
        <div className="text-xs text-[var(--mq-text-3)]">
          {loading ? t("loading") : t("noData")}
        </div>
      </div>
    );
  }

  const { inputs, recommended, projected, warnings } = data;
  const sliderMin = recommended.order_size_usdt_min || 1;
  const sliderMax = recommended.order_size_usdt_max || sliderMin;
  const sliderEffective = Math.max(sliderMin, Math.min(slider, sliderMax));

  return (
    <div className="mq-panel p-4" data-testid="capital-allocator-panel">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="mq-heading text-sm tracking-widest uppercase">
            {t("capitalTitle")}
          </h3>
          <div className="mq-label mt-1" style={{ color: "var(--mq-text-3)" }}>
            {t("capitalAdvisoryNote")}
          </div>
        </div>
        <button
          data-testid="capital-allocator-refresh"
          className="mq-btn"
          onClick={load}
          disabled={loading}
          style={{ padding: "0.3rem 0.65rem" }}
        >
          ↻
        </button>
      </div>

      {/* live balances */}
      <div className="grid grid-cols-2 gap-3 mb-3 text-xs">
        <div>
          <div className="mq-label">{t("capitalUsdtBalance")}</div>
          <div className="font-mono text-base" data-testid="capital-usdt-balance">
            ${inputs.usdt_balance.toLocaleString(undefined, { maximumFractionDigits: 2 })}
          </div>
        </div>
        <div>
          <div className="mq-label">{t("capitalTokenBalance")}</div>
          <div className="font-mono text-base" data-testid="capital-token-balance">
            {inputs.token_balance.toLocaleString(undefined, { maximumFractionDigits: 0 })}
            <span className="mq-label ml-1" style={{ color: "var(--mq-text-3)" }}>
              (~${inputs.token_value_usd.toLocaleString(undefined, { maximumFractionDigits: 0 })})
            </span>
          </div>
        </div>
        <div>
          <div className="mq-label">{t("capitalLayersRefresh")}</div>
          <div className="font-mono text-sm">
            {inputs.order_layers} · {inputs.refresh_rate_sec}s
          </div>
        </div>
        <div>
          <div className="mq-label">{t("capitalFillRate")}</div>
          <div className="font-mono text-sm">
            {(inputs.fill_rate * 100).toFixed(1)}%
          </div>
        </div>
      </div>

      {/* recommendation block */}
      <div
        className="border-t pt-3 mb-3"
        style={{ borderColor: "var(--mq-border)" }}
      >
        <div className="flex items-center justify-between text-xs mb-1">
          <span className="mq-label">{t("capitalCurrentSize")}</span>
          <span className="font-mono" data-testid="capital-current-size">
            ${inputs.current_order_size_usdt}/layer
          </span>
        </div>
        <div className="flex items-center justify-between text-xs mb-1">
          <span className="mq-label">{t("capitalAiMax")}</span>
          <span
            className="font-mono"
            style={{ color: "var(--mq-cyan)" }}
            data-testid="capital-ai-max"
          >
            ${recommended.order_size_usdt_max}/layer
          </span>
        </div>
        <div className="flex items-center justify-between text-xs mb-3">
          <span className="mq-label">{t("capitalProjectedVolume")}</span>
          <span
            className="font-mono"
            style={{ color: "var(--mq-green)" }}
            data-testid="capital-projected-volume"
          >
            ${projected.volume_usd.toLocaleString(undefined, { maximumFractionDigits: 0 })}
            <span className="mq-label ml-1" style={{ color: "var(--mq-text-3)" }}>
              ({projected.vs_target_pct.toFixed(0)}% {t("capitalOfTarget")})
            </span>
          </span>
        </div>

        <div className="flex items-center gap-3">
          <span className="mq-label text-xs">${sliderMin}</span>
          <input
            data-testid="capital-allocator-slider"
            type="range"
            min={sliderMin}
            max={Math.max(sliderMin, sliderMax)}
            step="0.5"
            value={sliderEffective}
            onChange={(e) => setSlider(Number(e.target.value))}
            className="flex-1"
            style={{ accentColor: "var(--mq-green)" }}
          />
          <span className="mq-label text-xs">${sliderMax}</span>
          <span
            className="font-mono font-bold ml-2"
            data-testid="capital-allocator-slider-value"
            style={{ minWidth: 56, textAlign: "right" }}
          >
            ${sliderEffective.toFixed(1)}
          </span>
        </div>

        <button
          data-testid="capital-allocator-apply"
          className="mq-btn mq-btn-primary w-full mt-3"
          onClick={apply}
          disabled={applying || sliderEffective === inputs.current_order_size_usdt}
        >
          {applying ? t("applying") : t("capitalApply")}
        </button>
      </div>

      {/* warnings */}
      {warnings && warnings.length > 0 && (
        <div className="space-y-1.5">
          {warnings.map((w, i) => (
            <div
              key={`${w.code}-${i}`}
              data-testid={`capital-warning-${w.code}`}
              className="text-[11px] px-3 py-1.5 border-l-2"
              style={{
                borderColor: WARN_COLOR[w.level] || "var(--mq-yellow)",
                color: WARN_COLOR[w.level] || "var(--mq-yellow)",
                background: "rgba(255,255,255,0.02)",
              }}
            >
              ⚠ {w.message}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
